import React from 'react';

import { FieldArray, useFormikContext } from 'formik';

import { Typography } from 'homer-web-app-components';

import classNames from 'classnames';

import { LANGUAGES, LANGUAGES_NAMES } from '../../../../../constants/languages';
import { IMAGE_MIME_TYPES } from '../../../../../constants/tasks';
import {
  Button,
  MutateCategory,
  Tile,
  TileHeader,
  TileMain,
} from '../../../../Common';
import Errors from '../../../Errors';
import PushTask from '../../../PushTask';
import MutatePionLegend from '../MutatePion/MutatePionLegend';

import MutateTask from './MutateTask';
import MutateTasksListColor from './MutateTasksListColor';
import MutateTasksListIcon from './MutateTasksListIcon';
import MutateTasksListImage from './MutateTasksListImage';
import MutateTasksListInput from './MutateTasksListInput';
import MutateTasksListLegend from './MutateTasksListLegend';
import useMutateTasksListTask from './hooks/useMutateTasksListTask';

const MutateTasksList = () => {
  const { values, errors, setFieldValue, setFieldTouched } = useFormikContext();
  const { addTask, removeTask } = useMutateTasksListTask();

  return (
    <>
      {Object.keys(errors).length > 0 && <Errors errors={errors} />}
      <Tile theme={Tile.THEME.WHITE}>
        <TileHeader
          style={{ marginBottom: '4rem', display: 'flex' }} // @todo Move `marginBottom` to an util css
        >
          <Typography
            component="h2"
            variant={Typography.THEME.VARIANTS.TITLE_HUGE}
          >
            Entry
          </Typography>
          {values.id && <PushTask />}
        </TileHeader>
        <TileMain style={{ padding: 0 }}>
          {Object.values(LANGUAGES).map((language) => (
            <React.Fragment key={language}>
              <MutateTasksListLegend
                className={classNames({
                  'mutate-card-legend-error':
                    !values.data.title[language] ||
                    (values.data.title[language] && errors['.title']),
                })}
              >
                {LANGUAGES_NAMES[language]}
              </MutateTasksListLegend>
              <MutateTasksListInput
                style={{ marginBottom: '.5rem' }}
                name={`data.title.${language}`}
                data-testid={`data.title.${language}`}
                placeholder="Title"
              />
              <MutateTasksListInput
                style={{ marginBottom: '.875rem' }}
                name={`data.alterTitle.${language}`}
                data-testid={`data.alterTitle.${language}`}
                placeholder="Alter title"
              />
              <MutateTasksListInput
                style={{ marginBottom: '.875rem' }}
                name={`data.description.${language}`}
                data-testid={`data.description.${language}`}
                placeholder="Description"
              />
              <MutateTasksListColor
                style={{ marginBottom: '.875rem' }}
                name={`data.color.${language}`}
                data-testid={`data.color.${language}`}
              />
              <MutateTasksListIcon
                style={{ marginBottom: '.875rem' }}
                name={`data.icon.${language}`}
              />
              <MutateTasksListImage
                style={{ marginBottom: '1rem' }}
                language={language}
                type={Object.values(IMAGE_MIME_TYPES)}
                name={`data.image.${language}`}
                data-testid={`data.image.${language}`}
                onSubmit={(value) => {
                  Object.values(LANGUAGES)
                    .filter((l) => l !== language)
                    .forEach((l) => {
                      if (!values.data.image[l]) {
                        setFieldValue(`data.image.${l}`, value);
                        setFieldTouched(`data.image.${l}`, true);
                      }
                    });
                }}
              />
            </React.Fragment>
          ))}
        </TileMain>
        <TileMain style={{ paddingBottom: '10px' }}>
          <h5>Categories</h5>
          <MutateCategory
            name="data.categories"
            value={values.data.categories}
          />
        </TileMain>
      </Tile>
      <Tile theme={Tile.THEME.GIN}>
        <TileHeader style={{ display: 'flex', marginBottom: '4rem' }}>
          <Typography
            component="h2"
            variant={Typography.THEME.VARIANTS.TITLE_HUGE}
          >
            Tasks
          </Typography>
        </TileHeader>
        {Object.values(LANGUAGES).map((language) => (
          <React.Fragment key={language}>
            <MutatePionLegend>{LANGUAGES_NAMES[language]}</MutatePionLegend>
            <FieldArray name={`data.tasks.${language}`}>
              {(fieldArrayHelpers) => (
                <>
                  {values.data.tasks &&
                    values.data.tasks[language] &&
                    values.data.tasks[language].length > 0 &&
                    values.data.tasks[language].map((task, index) => (
                      <TileMain
                        className="pion-block"
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                      >
                        <div style={{ display: 'flex' }}>
                          <Button
                            style={{ alignSelf: 'center', marginLeft: 'auto' }}
                            theme={Button.THEME.FLAMINGO}
                            sm
                            type="button"
                            onClick={() => {
                              removeTask(fieldArrayHelpers, index);
                            }}
                            data-testid="remove-tsk"
                          >
                            Remove task
                          </Button>
                        </div>
                        <MutateTask
                          task={task}
                          name={`data.tasks.${language}[${index}]`}
                          language={language}
                        />
                      </TileMain>
                    ))}
                  <TileMain style={{ marginBottom: '10px' }}>
                    <Button
                      lg
                      block
                      type="button"
                      style={{ textAlign: 'center' }}
                      onClick={() => addTask(fieldArrayHelpers)}
                      data-testid={`add-task-${language}`}
                    >
                      Add Task
                    </Button>
                  </TileMain>
                </>
              )}
            </FieldArray>
          </React.Fragment>
        ))}
      </Tile>
    </>
  );
};

export default MutateTasksList;
